import React from 'react';
import DataTableWithButton from '../../shared/component/dataTable/DataTableWithButton';
import {getActualOrderAmountTone, getActualOrderAmountHighlightColor, formatOrderAmountWithUnit} from './PurchaseRequisitionActualOrderUtils';
import {RETURN_REASON_CODE} from '../../shared/utils/Constants';
import {
    DN_ONLY_DISPOSITION_OPTIONS,
    normalizeDnOnlyDisposition
} from '../purchaseorder/PurchaseOrderCompareUtils';

const CHEAPER_COST_HIGHLIGHT = '#e81123';

const STATUS_LABELS = {
    MATCHED: 'Matched',
    SHORT: 'Short delivery',
    OVER: 'Over delivery',
    MISSING: 'Missing on DN',
    UNEXPECTED: 'Not on PO'
};

export default class PurchaseRequisitionItemDataTable extends DataTableWithButton {
    getMoneyCellHighlight = (rowData, column) => {
        if (this.props.highlightCheaperCost === false) {
            return null;
        }
        if (column.field === 'currentCost' && rowData.cheaperCostAvailable === true) {
            return CHEAPER_COST_HIGHLIGHT;
        }
        if (column.field === 'actualOrderAmount') {
            return getActualOrderAmountHighlightColor(rowData);
        }
        return null;
    };

    longNameBody = (rowData) => {
        const text = rowData?.longName || rowData?.itemName || '';
        return (
            <span
                style={{whiteSpace: 'normal', wordBreak: 'break-word'}}
                title={text}
            >
                {text}
            </span>
        );
    };

    actualOrderHighlightBody = (rowData) => {
        const value = rowData?.actualOrderAmount;
        const display = value == null || value === '' ? '—' : value;
        const tone = getActualOrderAmountTone(rowData);
        if (!tone) {
            return <span>{display}</span>;
        }
        return (
            <span className={`pr-actual-qty pr-actual-qty--${tone}`} title={this.getActualOrderTitle(tone)}>
                {display}
            </span>
        );
    };

    actualOrderWithUnitBody = (rowData) => {
        const display = formatOrderAmountWithUnit(rowData?.actualOrderAmount, rowData?.orderUnitOfMeasure);
        const tone = getActualOrderAmountTone(rowData);
        if (!tone) {
            return <span>{display}</span>;
        }
        return (
            <span className={`pr-actual-qty pr-actual-qty--${tone}`} title={this.getActualOrderTitle(tone)}>
                {display}
            </span>
        );
    };

    getActualOrderTitle = (tone) => {
        if (tone === 'over-max') {
            return 'Actual order amount exceeds Max Stock';
        }
        if (tone === 'over-capacity') {
            return 'Current stock + actual order exceeds Max Stock';
        }
        if (tone === 'below-recommended') {
            return 'Total Pieces is below Rec. Qty';
        }
        if (tone === 'above-recommended') {
            return 'Total Pieces is above Rec. Qty';
        }
        return '';
    };

    getCompareStatusLabels = () => this.props.compareStatusLabels || STATUS_LABELS;

    getDeliveryCompareTone = (matchStatus) => {
        if (matchStatus === 'SHORT' || matchStatus === 'MISSING' || matchStatus === 'UNEXPECTED') {
            return 'short';
        }
        if (matchStatus === 'OVER') {
            return 'over';
        }
        return null;
    };

    deliveryCompareStatusBody = (rowData) => {
        const status = rowData?.matchStatus;
        const labels = this.getCompareStatusLabels();
        const label = labels[status] || status || '';
        const tone = this.getDeliveryCompareTone(status);
        if (!tone) {
            return <span>{label}</span>;
        }
        return <span className={`dn-compare-chip dn-compare-chip--${tone}`}>{label}</span>;
    };

    deliveryCompareWarningBody = (rowData) => {
        const warning = rowData?.warning || '';
        const tone = this.getDeliveryCompareTone(rowData?.matchStatus);
        if (!warning) {
            return <span>—</span>;
        }
        if (!tone) {
            return <span title={warning}>{warning}</span>;
        }
        return (
            <span className={`dn-compare-warning dn-compare-warning--${tone}`} title={warning}>
                {warning}
            </span>
        );
    };

    deliveryCompareDamagedBody = (rowData) => {
        if (this.props.inlineEditEnabled && typeof this.props.onInlineFieldChange === 'function') {
            return this.inlineNumberBody(rowData, 'damagedQty', rowData?.damagedQty);
        }
        const damaged = Number(rowData?.damagedQty);
        const display = rowData?.damagedQty == null || rowData?.damagedQty === '' ? 0 : rowData.damagedQty;
        const isAlert = !Number.isNaN(damaged) && damaged > 0;
        return (
            <span className={isAlert ? 'dn-compare-damaged dn-compare-damaged--alert' : ''}>
                {display}
            </span>
        );
    };

    /**
     * Invoice compare: Only-on-DN disposition (Accepted / Scanning error / Wrong item).
     * Other rows show em dash.
     */
    invoiceDnDispositionBody = (rowData) => {
        const onlyOnDn = rowData?.isOnlyOnDn === true || rowData?.matchStatus === 'UNEXPECTED';
        if (!onlyOnDn) {
            return <span className="text-muted">—</span>;
        }
        const editable = this.props.invoiceDnDispositionEditable !== false
            && typeof this.props.onInvoiceDnDispositionChange === 'function';
        const value = normalizeDnOnlyDisposition(rowData?.disposition);
        if (!editable) {
            const match = DN_ONLY_DISPOSITION_OPTIONS.find(o => o.value === value);
            return <span>{match ? match.name : value}</span>;
        }
        return (
            <select
                className="form-control form-control-sm pr-inline-edit-input"
                value={value}
                title="Only on DN: Accepted (nothing) · Scanning error (reverse stock, no return) · Wrong item (return, no reverse)"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                    this.props.onInvoiceDnDispositionChange(
                        rowData,
                        normalizeDnOnlyDisposition(e.target.value)
                    );
                }}
            >
                {DN_ONLY_DISPOSITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
            </select>
        );
    };

    inlineDeliveredQtyBody = (rowData) => {
        if (this.props.inlineEditEnabled && typeof this.props.onInlineFieldChange === 'function') {
            return this.inlineNumberBody(rowData, 'deliveredQty', rowData?.deliveredQty);
        }
        const value = rowData?.deliveredQty;
        return <span>{value == null || value === '' ? '—' : value}</span>;
    };

    inlineNumberBody = (rowData, field, rawValue) => {
        const display = rawValue == null || rawValue === '' ? '' : rawValue;
        return (
            <input
                type="number"
                className="form-control form-control-sm pr-inline-edit-input"
                min="0"
                step="any"
                value={display}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                    if (typeof this.props.onInlineFieldChange === 'function') {
                        this.props.onInlineFieldChange(rowData, field, e.target.value);
                    }
                }}
            />
        );
    };

    inlineReturnQtyBody = (rowData) => {
        if (this.props.inlineEditEnabled && typeof this.props.onInlineFieldChange === 'function') {
            return this.inlineNumberBody(rowData, 'actualOrderAmount', rowData?.actualOrderAmount);
        }
        const value = rowData?.actualOrderAmount;
        return <span>{value == null || value === '' ? '—' : value}</span>;
    };

    inlineReasonCodeBody = (rowData) => {
        if (!this.props.inlineEditEnabled || typeof this.props.onInlineFieldChange !== 'function') {
            const code = rowData?.reasonCode;
            if (!code) {
                return <span>—</span>;
            }
            const options = this.props.reasonOptions || RETURN_REASON_CODE;
            const match = (options || []).find(o => String(o.value) === String(code));
            return <span>{match ? match.name : code}</span>;
        }
        const options = this.props.reasonOptions || RETURN_REASON_CODE;
        return (
            <select
                className="form-control form-control-sm pr-inline-edit-input"
                value={rowData?.reasonCode || ''}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                    this.props.onInlineFieldChange(rowData, 'reasonCode', e.target.value);
                }}
            >
                <option value=""/>
                {(options || []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
            </select>
        );
    };

    inlineDescriptionBody = (rowData) => {
        if (!this.props.inlineEditEnabled || typeof this.props.onInlineFieldChange !== 'function') {
            return <span title={rowData?.description || ''}>{rowData?.description || '—'}</span>;
        }
        return (
            <input
                type="text"
                className="form-control form-control-sm pr-inline-edit-input"
                maxLength={50}
                value={rowData?.description || ''}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onChange={(e) => {
                    this.props.onInlineFieldChange(rowData, 'description', e.target.value);
                }}
            />
        );
    };
}
